import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  GraduationCap, Briefcase, Rocket, TrendingUp, ArrowRight,
  Calendar, MapPin, Users, Clock, Award, Star, Search,
  ChevronRight, Filter, X, ChevronDown, Grid, List
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseListingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedMode, setSelectedMode] = useState(searchParams.get('mode') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'popular');

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchCourses();
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedMode) params.set('mode', selectedMode);
    if (sortBy !== 'popular') params.set('sort', sortBy);
    setSearchParams(params);
  }, [page, selectedCategory, selectedMode, sortBy]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/future-skills/categories`);
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('skip', ((page - 1) * ITEMS_PER_PAGE).toString());
      params.set('limit', ITEMS_PER_PAGE.toString());
      if (selectedCategory) params.set('category', selectedCategory);
      if (searchQuery) params.set('search', searchQuery);
      
      const res = await axios.get(`${API}/future-skills/courses?${params.toString()}`);
      let coursesData = res.data.courses || [];
      
      // Apply client-side mode filter and sorting
      if (selectedMode) {
        coursesData = coursesData.filter(c => c.mode === selectedMode);
      }
      
      // Sort
      switch (sortBy) {
        case 'price_low':
          coursesData.sort((a, b) => a.price - b.price);
          break;
        case 'price_high':
          coursesData.sort((a, b) => b.price - a.price);
          break;
        case 'newest':
          coursesData.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          break;
        case 'starting_soon':
          coursesData.sort((a, b) => new Date(a.start_date || '2099-01-01') - new Date(b.start_date || '2099-01-01'));
          break;
        default: // popular - by enrollment count
          coursesData.sort((a, b) => (b.enrollment_count || 0) - (a.enrollment_count || 0));
      }
      
      setCourses(coursesData);
      setTotalCount(res.data.total || coursesData.length);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCourses();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedMode('');
    setSortBy('popular');
    setPage(1);
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const getCategoryIcon = (id) => {
    const icons = {
      summer_internship: Briefcase,
      placement_accelerator: Rocket,
      upskilling: TrendingUp
    };
    return icons[id] || GraduationCap;
  };

  const getCategoryColor = (id) => {
    const colors = {
      summer_internship: 'amber',
      placement_accelerator: 'purple',
      upskilling: 'cyan'
    };
    return colors[id] || 'gray';
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-cyan-500/10 to-purple-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-amber-500/5 to-rose-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/future-skills" className="hover:text-cyan-400 transition-colors">
              School of Future Skills
            </Link>
            <ChevronRight size={14} />
            <span className="text-gray-300">All Programs</span>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                  All Programs
                </span>
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl">
                Explore our curated collection of industry-focused programs designed to accelerate your career
              </p>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="view-grid-btn"
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-500 hover:text-white'
                }`}
                data-testid="view-list-btn"
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Search & Filter Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search programs by title, description..."
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                  data-testid="search-input"
                />
              </div>
            </form>
            
            {/* Filter Toggle (Mobile) */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-300"
            >
              <Filter size={18} />
              Filters
              {(selectedCategory || selectedMode) && (
                <span className="w-2 h-2 bg-cyan-400 rounded-full" />
              )}
            </button>
            
            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-4">
              {/* Category Filter */}
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                  className="appearance-none px-4 py-3 pr-10 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
              </div>
              
              {/* Mode Filter */}
              <div className="relative">
                <select
                  value={selectedMode}
                  onChange={(e) => { setSelectedMode(e.target.value); setPage(1); }}
                  className="appearance-none px-4 py-3 pr-10 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
                  data-testid="mode-filter"
                >
                  <option value="">All Modes</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="hybrid">Hybrid</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
              </div>
              
              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                  className="appearance-none px-4 py-3 pr-10 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
                  data-testid="sort-filter"
                >
                  <option value="popular">Most Popular</option>
                  <option value="newest">Newest First</option>
                  <option value="starting_soon">Starting Soon</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
              </div>
              
              {/* Clear Filters */}
              {(selectedCategory || selectedMode || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} />
                  Clear
                </button>
              )}
            </div>
          </div>
          
          {/* Mobile Filters Panel */}
          {showFilters && (
            <div className="md:hidden bg-white/5 border border-white/10 rounded-xl p-4 mb-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Mode</label>
                <select
                  value={selectedMode}
                  onChange={(e) => { setSelectedMode(e.target.value); setPage(1); }}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white"
                >
                  <option value="">All Modes</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white"
                >
                  <option value="popular">Most Popular</option>
                  <option value="newest">Newest First</option>
                  <option value="starting_soon">Starting Soon</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
              </div>
              <div className="flex justify-between pt-2">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
          
          {/* Category Quick Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={() => { setSelectedCategory(''); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !selectedCategory
                  ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="filter-all"
            >
              All Programs
            </button>
            {categories.map(cat => {
              const Icon = getCategoryIcon(cat.id);
              const color = getCategoryColor(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat.id
                      ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/30`
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  <Icon size={16} />
                  {cat.name}
                </button>
              );
            })}
          </div>
          
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-400">
              Showing <span className="text-white font-medium">{courses.length}</span> of{' '}
              <span className="text-white font-medium">{totalCount}</span> programs
            </p>
          </div>
          
          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
            </div>
          ) : courses.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto bg-white/5 rounded-2xl flex items-center justify-center mb-6">
                <GraduationCap className="text-gray-500" size={40} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No programs found</h3>
              <p className="text-gray-400 mb-6">Try adjusting your filters or search query</p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            /* Course Grid/List */
            <div className={viewMode === 'grid' 
              ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {courses.map((course) => (
                viewMode === 'grid' 
                  ? <CourseCard key={course.id} course={course} />
                  : <CourseListItem key={course.id} course={course} />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="prev-page-btn"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        page === pageNum
                          ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="next-page-btn"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need help choosing the right program?
          </h2>
          <p className="text-gray-400 mb-8">
            Our counselors can help you find the perfect program based on your goals and experience
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
            data-testid="talk-to-advisor-btn"
          >
            Talk to an Advisor
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Course Card Component (Grid View)
const CourseCard = ({ course }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCategoryColor = (category) => {
    const colors = {
      summer_internship: 'amber',
      placement_accelerator: 'purple',
      upskilling: 'cyan'
    };
    return colors[category] || 'gray';
  };

  const color = getCategoryColor(course.category);

  return (
    <Link
      to={`/future-skills/course/${course.slug}`}
      className="group bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all hover:transform hover:scale-[1.02]"
      data-testid={`course-card-${course.id}`}
    >
      {/* Banner */}
      <div className={`h-40 bg-gradient-to-br from-${color}-500/30 to-${color}-600/20 relative`}>
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 bg-${color}-500/20 text-${color}-400 rounded-full text-xs font-medium`}>
            {course.category_name || course.category}
          </span>
        </div>
        {course.is_featured && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-medium flex items-center gap-1">
              <Star size={12} />
              Featured
            </span>
          </div>
        )}
        {course.mode && (
          <div className="absolute bottom-4 left-4">
            <span className="px-2 py-1 bg-black/40 text-white rounded text-xs font-medium backdrop-blur-sm">
              {course.mode === 'online' ? '🌐 Online' : course.mode === 'offline' ? '🏢 Offline' : '🔄 Hybrid'}
            </span>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors line-clamp-2">
          {course.title}
        </h3>
        
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
          {course.description}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4 text-xs text-gray-500">
          {course.duration_weeks && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {course.duration_weeks} weeks
            </span>
          )}
          {course.start_date && (
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {new Date(course.start_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {(course.seats_left || course.max_seats) && (
            <span className="flex items-center gap-1">
              <Users size={12} />
              {course.seats_left || course.max_seats} seats
            </span>
          )}
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-white/10">
          <div>
            <span className="text-2xl font-bold text-white">{formatPrice(course.price)}</span>
          </div>
          <span className="text-cyan-400 text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1">
            View Details
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
};

// Course List Item Component (List View)
const CourseListItem = ({ course }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCategoryColor = (category) => {
    const colors = {
      summer_internship: 'amber',
      placement_accelerator: 'purple',
      upskilling: 'cyan'
    };
    return colors[category] || 'gray';
  };

  const color = getCategoryColor(course.category);

  return (
    <Link
      to={`/future-skills/course/${course.slug}`}
      className="group flex flex-col md:flex-row bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all"
      data-testid={`course-list-item-${course.id}`}
    >
      {/* Banner */}
      <div className={`w-full md:w-64 h-32 md:h-auto bg-gradient-to-br from-${color}-500/30 to-${color}-600/20 relative flex-shrink-0`}>
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 bg-${color}-500/20 text-${color}-400 rounded-full text-xs font-medium`}>
            {course.category_name || course.category}
          </span>
        </div>
        {course.is_featured && (
          <div className="absolute top-3 right-3">
            <Star className="text-amber-400" size={16} />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 p-5 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-4 mb-2">
            <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors line-clamp-1">
              {course.title}
            </h3>
            <span className="text-xl font-bold text-white whitespace-nowrap">{formatPrice(course.price)}</span>
          </div>
          
          <p className="text-gray-400 text-sm mb-3 line-clamp-2">
            {course.description}
          </p>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            {course.mode && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {course.mode === 'online' ? 'Online' : course.mode === 'offline' ? 'Offline' : 'Hybrid'}
              </span>
            )}
            {course.duration_weeks && (
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {course.duration_weeks} weeks
              </span>
            )}
            {course.start_date && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                Starts {new Date(course.start_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
              </span>
            )}
            {course.enrollment_count > 0 && (
              <span className="flex items-center gap-1">
                <Users size={12} />
                {course.enrollment_count} enrolled
              </span>
            )}
          </div>
          
          <span className="text-cyan-400 text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1">
            View Details
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default CourseListingPage;
